/**
 * Product page enhancements – sticky ATC, social proof, bundle progress
 * Dawn-compatible: uses PUB_SUB_EVENTS.variantChange and cartUpdate; no changes to product-form.js
 */
(function () {
  'use strict';

  if (typeof PUB_SUB_EVENTS === 'undefined' || typeof subscribe === 'undefined') return;

  const productInfo = document.querySelector('product-info[id^="MainProduct"]');
  if (!productInfo) return;

  const sectionId = productInfo.dataset.section;
  const productId = productInfo.dataset.productId;

  /**
   * Injects seeded social proof (rating, units sold). Low stock uses real variant inventory from Liquid / variantChange.
   * Uses shared getSeededSocialProof from social-proof-utils.js (theme settings for min-max).
   */
  function initSocialProof() {
    if (!productId || typeof window.getSeededSocialProof !== 'function') return;
    const data = window.getSeededSocialProof(productId);

    const ratingEl = productInfo.querySelector('.product-enhancements__rating-value');
    if (ratingEl) ratingEl.textContent = data.rating.toFixed(1);

    const countEl = productInfo.querySelector('.product-enhancements__rating-count');
    if (countEl) countEl.textContent = data.ratingCount;

    const unitsEl = productInfo.querySelector('.product-enhancements__units-sold');
    if (unitsEl) unitsEl.textContent = data.unitsSold;
    /* Low stock is set by Liquid from real inventory; updated on variant change below */
  }

  function updateLowStockFromVariant(variant) {
    const lowStockWrap = productInfo.querySelector('.product-enhancements__low-stock');
    const lowStockValue = productInfo.querySelector('.product-enhancements__low-stock-value');
    if (!lowStockWrap || !lowStockValue) return;
    const tracked = variant && variant.inventory_management === 'shopify';
    const qty = (variant && variant.inventory_quantity) || 0;
    if (tracked && qty > 0) {
      lowStockValue.textContent = qty;
      lowStockWrap.classList.remove('product-enhancements__low-stock--hidden');
      lowStockWrap.style.display = '';
    } else {
      lowStockWrap.classList.add('product-enhancements__low-stock--hidden');
      lowStockWrap.style.display = 'none';
    }
  }

  /**
   * Sticky ATC: show when main ATC is out of view; keep in sync with variant/price.
   * Submit button triggers the main product form so Dawn add-to-cart logic is unchanged.
   */
  function initStickyAtc() {
    const bar = document.querySelector('.sticky-atc-bar');
    const mainForm = productInfo.querySelector('form[data-type="add-to-cart-form"]');
    if (!bar || !mainForm) return;

    const priceBlock = document.getElementById(`price-${sectionId}`);
    const variantSelects = productInfo.querySelector('variant-selects');
    const variantJsonEl = variantSelects ? variantSelects.querySelector('[data-selected-variant]') : null;
    const titleEl = productInfo.querySelector('.product__title h1, .product__title h2');
    const titleText = titleEl ? titleEl.textContent.trim() : '';
    const submitBtn = bar.querySelector('.sticky-atc-bar__submit');

    function setStickyVariant(variant) {
      if (!variant) return;
      const variantLabel = productInfo.querySelector('variant-selects [data-selected-value]');
      const variantText = variant.title && variant.title !== 'Default Title' ? variant.title : (variantLabel ? variantLabel.textContent.trim() : '');
      const variantDisplay = bar.querySelector('.sticky-atc-bar__variant');
      if (variantDisplay) variantDisplay.textContent = variantText;
      const priceDisplay = bar.querySelector('.sticky-atc-bar__price');
      if (priceDisplay && variant.price !== undefined) {
        const formatted = window.Shopify?.formatMoney?.(variant.price) ?? `${(variant.price / 100).toFixed(2)}`;
        priceDisplay.textContent = formatted;
      }
      if (submitBtn) {
        if (variant.available === false) {
          submitBtn.disabled = true;
          submitBtn.textContent = window.variantStrings?.soldOut ?? 'Sold out';
        } else {
          submitBtn.disabled = false;
          submitBtn.textContent = window.variantStrings?.addToCart ?? 'Add to cart';
        }
      }
    }

    function syncFromDom() {
      const titleDisplay = bar.querySelector('.sticky-atc-bar__title');
      if (titleDisplay) titleDisplay.textContent = titleText;
      if (variantJsonEl) {
        try {
          const variant = JSON.parse(variantJsonEl.textContent);
          setStickyVariant(variant);
        } catch (_) {}
      }
      if (priceBlock && !variantJsonEl) {
        const priceDisplay = bar.querySelector('.sticky-atc-bar__price');
        if (priceDisplay) priceDisplay.innerHTML = priceBlock.innerHTML;
      }
    }

    syncFromDom();

    if (submitBtn) {
      submitBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (submitBtn.disabled) return;
        mainForm.requestSubmit();
      });
    }

    const mainAtc = productInfo.querySelector('.product-form__submit, [type="submit"][name="add"]');
    const sentinel = mainAtc || mainForm;
    if (sentinel) {
      const observer = new IntersectionObserver(
        (entries) => {
          const e = entries[0];
          if (!e) return;
          bar.classList.toggle('is-visible', !e.isIntersecting);
        },
        { root: null, rootMargin: '0px', threshold: 0 }
      );
      observer.observe(sentinel);
    }

    subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      if (!payload?.data?.sectionId || payload.data.sectionId !== sectionId) return;
      if (payload.data.variant) {
        setStickyVariant(payload.data.variant);
        updateLowStockFromVariant(payload.data.variant);
      }
    });
  }

  /**
   * Bundle progress: show "Buy X to unlock" or "View Bundle Offers"; update on cart change
   */
  function initBundleProgress() {
    const bundleEl = productInfo.querySelector('.product-enhancements__bundle');
    if (!bundleEl) return;

    const collectionIdsJson = bundleEl.dataset.offerCollectionIds;
    const minimumQty = parseInt(bundleEl.dataset.offerMinimumQty, 10) || 3;
    const collectionUrl = bundleEl.dataset.offerCollectionUrl || '#';

    if (!collectionIdsJson) {
      const cta = bundleEl.querySelector('.product-enhancements__bundle-cta');
      if (cta) cta.href = collectionUrl;
      return;
    }

    let offerCollectionIds = [];
    try {
      offerCollectionIds = JSON.parse(collectionIdsJson);
    } catch (_) {}

    function updateProgress() {
      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => {
          let total = 0;
          (cart.items || []).forEach((item) => {
            if (offerCollectionIds.indexOf(item.product_id) !== -1) total += item.quantity || 0;
          });
          const progressFill = bundleEl.querySelector('.product-enhancements__bundle-progress-fill');
          const progressText = bundleEl.querySelector('.product-enhancements__bundle-progress-text');
          if (progressFill) {
            const pct = Math.min(100, (total / minimumQty) * 100);
            progressFill.style.width = pct + '%';
          }
          if (progressText) {
            const left = Math.max(0, minimumQty - total);
            progressText.textContent = left > 0 ? `Buy ${left} more to unlock offer` : 'Offer unlocked!';
          }
        })
        .catch(() => {});
    }

    updateProgress();
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => updateProgress());
  }

  function initGallerySlider() {
    const viewer = document.querySelector('[id^="GalleryViewer-"]');
    if (!viewer) return;

    viewer.addEventListener('slideChanged', (e) => {
      const index = e.detail.currentPage - 1;
      const dots = viewer.querySelectorAll('.slider-dot-item');
      dots.forEach((dot, idx) => {
        dot.classList.toggle('active', idx === index);
      });
    });

    const dots = viewer.querySelectorAll('.slider-dot-item');
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.index, 10);
        const slider = viewer.querySelector('[id^="Slider-Gallery-"]');
        if (!slider) return;
        const slides = slider.querySelectorAll('.slider__slide');
        if (slides[idx]) {
          slider.scrollTo({
            left: slides[idx].offsetLeft,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  function init() {
    initSocialProof();
    initStickyAtc();
    initBundleProgress();
    initGallerySlider();
    /* Sync low stock with initial variant (in case Liquid and JS differ) */
    const variantJsonEl = productInfo.querySelector('variant-selects [data-selected-variant]');
    if (variantJsonEl) {
      try {
        const variant = JSON.parse(variantJsonEl.textContent);
        updateLowStockFromVariant(variant);
      } catch (_) {}
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
